import React, { useEffect, useMemo, useRef } from "react";
import { useAuth } from "@/lib/AuthContext";
import * as analytics from "@/lib/analytics";
import { useLandingSections } from "@/api/hooks";
import GiveawayHeader from "@/components/giveaway/GiveawayHeader";
import GiveawayFooter from "@/components/giveaway/GiveawayFooter";
import LandingHero from "@/components/landingV3/LandingHero";
import ProblemStatement from "@/components/landingV3/ProblemStatement";
import FaceValueAccess from "@/components/landingV3/FaceValueAccess";
import HowItWorks from "@/components/landingV3/HowItWorks";
import WhatsInside from "@/components/landingV3/WhatsInside";
import WhatWeAreNot from "@/components/landingV3/WhatWeAreNot";
import WhyItWorks from "@/components/landingV3/WhyItWorks";
import LandingFAQ from "@/components/landingV3/LandingFAQ";
import LandingCTA from "@/components/landingV3/LandingCTA";

// Maps server section_key → React component. Components receive `onCTA` only
// where they have a primary CTA button (Hero / WhatsInside / LandingCTA).
// To add a new section, add the component file under landingV3/ and register
// it here AND in migration 032 (or a new migration) so admins can toggle it.
const SECTION_RENDERERS = {
  LandingHero:      (props) => <LandingHero onCTA={props.onCTA} />,
  ProblemStatement: () => <ProblemStatement />,
  FaceValueAccess:  () => <FaceValueAccess />,
  HowItWorks:       () => <HowItWorks />,
  WhatsInside:      (props) => <WhatsInside onCTA={props.onCTA} />,
  WhatWeAreNot:     () => <WhatWeAreNot />,
  WhyItWorks:       () => <WhyItWorks />,
  LandingFAQ:       () => <LandingFAQ />,
  LandingCTA:       (props) => <LandingCTA onCTA={props.onCTA} />,
};

// Fallback layout if the API request fails — guarantees the page still renders
// something usable even if the server is unreachable. Matches the default
// order seeded by migration 032.
const FALLBACK_SECTIONS = [
  "LandingHero", "ProblemStatement", "FaceValueAccess", "HowItWorks",
  "WhatsInside", "WhatWeAreNot", "WhyItWorks", "LandingFAQ", "LandingCTA",
];

export default function LandingV3() {
  const { login } = useAuth();
  const { data, isError } = useLandingSections();

  const sections = useMemo(() => {
    if (isError || !data?.sections) {
      return FALLBACK_SECTIONS.filter((k) => SECTION_RENDERERS[k]);
    }
    return data.sections
      .map((s) => s.section_key)
      .filter((k) => SECTION_RENDERERS[k]);
  }, [data, isError]);

  const sectionRefs = useRef({});
  const setSectionRef = (name) => (el) => {
    if (el) sectionRefs.current[name] = el;
  };

  useEffect(() => {
    analytics.captureUTM();
    analytics.pageView("LandingV3");
    const detachScroll = analytics.attachScrollDepth();

    const observed = new Set();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const name = entry.target.dataset.section;
          if (entry.isIntersecting && name && !observed.has(name)) {
            observed.add(name);
            analytics.trackSectionView(name);
          }
        }
      },
      { threshold: 0.5 }
    );

    requestAnimationFrame(() => {
      for (const el of Object.values(sectionRefs.current)) {
        observer.observe(el);
      }
    });

    return () => {
      observer.disconnect();
      detachScroll();
    };
  }, [sections]);

  const onCTA = () => login();

  return (
    <div
      className="landing-v3 min-h-screen overflow-x-hidden w-full"
      dir="rtl"
      style={{ background: "#121212" }}
    >
      <GiveawayHeader onLoginClick={onCTA} />
      {sections.map((key) => {
        const Renderer = SECTION_RENDERERS[key];
        return (
          <div key={key} ref={setSectionRef(key)} data-section={key}>
            <Renderer onCTA={onCTA} />
          </div>
        );
      })}
      <GiveawayFooter />
    </div>
  );
}
